import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PriceAlerts = () => {
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      token: 'BTC',
      name: 'Bitcoin',
      condition: 'above',
      price: 45000,
      currentPrice: 43250.50,
      isActive: true,
      createdAt: new Date('2024-10-15')
    },
    {
      id: 2,
      token: 'AOGX',
      name: 'AOGX Token',
      condition: 'below',
      price: 0.85,
      currentPrice: 0.9234,
      isActive: true,
      createdAt: new Date('2024-10-16')
    },
    {
      id: 3,
      token: 'ETH',
      name: 'Ethereum',
      condition: 'above',
      price: 2500,
      currentPrice: 2456.78,
      isActive: false,
      createdAt: new Date('2024-10-14')
    }
  ]);

  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    token: '',
    condition: 'above',
    price: '',
    notificationMethod: 'email'
  });

  const tokenOptions = [
    { value: 'BTC', label: 'Bitcoin (BTC)' },
    { value: 'ETH', label: 'Ethereum (ETH)' },
    { value: 'AOGX', label: 'AOGX Token (AOGX)' },
    { value: 'ADA', label: 'Cardano (ADA)' },
    { value: 'SOL', label: 'Solana (SOL)' }
  ];

  const conditionOptions = [
    { value: 'above', label: 'Price goes above' },
    { value: 'below', label: 'Price goes below' }
  ];

  const notificationOptions = [
    { value: 'email', label: 'Email notification' },
    { value: 'push', label: 'Push notification' },
    { value: 'both', label: 'Email + Push' }
  ];

  const handleCreateAlert = () => {
    if (newAlert?.token && newAlert?.price) {
      const alert = {
        id: Date.now(),
        token: newAlert?.token,
        name: tokenOptions?.find(t => t?.value === newAlert?.token)?.label || newAlert?.token,
        condition: newAlert?.condition,
        price: parseFloat(newAlert?.price),
        currentPrice: Math.random() * 1000, // Mock current price
        isActive: true,
        createdAt: new Date()
      };
      
      setAlerts([...alerts, alert]);
      setNewAlert({ token: '', condition: 'above', price: '', notificationMethod: 'email' });
      setIsCreatingAlert(false);
    }
  };

  const toggleAlert = (id) => {
    setAlerts(alerts?.map(alert => 
      alert?.id === id ? { ...alert, isActive: !alert?.isActive } : alert
    ));
  };

  const deleteAlert = (id) => {
    setAlerts(alerts?.filter(alert => alert?.id !== id));
  };

  const getAlertStatus = (alert) => {
    const { condition, price, currentPrice } = alert;
    const isTriggered = condition === 'above' ? currentPrice >= price : currentPrice <= price;
    return isTriggered;
  };

  return (
    <div className="bg-card border border-border rounded-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Price Alerts</h3>
          <p className="text-sm text-muted-foreground">Get notified when prices hit your targets</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsCreatingAlert(!isCreatingAlert)}
          iconName={isCreatingAlert ? "X" : "Plus"}
          iconPosition="left"
        >
          {isCreatingAlert ? 'Cancel' : 'New Alert'}
        </Button>
      </div>
      {isCreatingAlert && (
        <div className="mb-6 p-4 bg-muted/50 rounded-brand-sm space-y-4">
          <h4 className="font-medium text-foreground">Create New Alert</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Select Token"
              options={tokenOptions}
              value={newAlert?.token}
              onChange={(value) => setNewAlert({ ...newAlert, token: value })}
              placeholder="Choose cryptocurrency"
            />
            
            <Select
              label="Condition"
              options={conditionOptions}
              value={newAlert?.condition}
              onChange={(value) => setNewAlert({ ...newAlert, condition: value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Target Price ($)"
              type="number"
              placeholder="0.00"
              value={newAlert?.price}
              onChange={(e) => setNewAlert({ ...newAlert, price: e?.target?.value })}
            />
            
            <Select
              label="Notification Method"
              options={notificationOptions}
              value={newAlert?.notificationMethod}
              onChange={(value) => setNewAlert({ ...newAlert, notificationMethod: value })}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setIsCreatingAlert(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateAlert}
              disabled={!newAlert?.token || !newAlert?.price}
              iconName="Bell"
              iconPosition="left"
            >
              Create Alert
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {alerts?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No price alerts set up yet</p>
            <p className="text-sm text-muted-foreground">Create your first alert to get notified of price movements</p>
          </div>
        ) : (
          alerts?.map((alert) => {
            const isTriggered = getAlertStatus(alert);
            
            return (
              <div
                key={alert?.id}
                className={`flex items-center justify-between p-4 rounded-brand-sm border transition-brand ${
                  isTriggered 
                    ? 'border-warning bg-warning/5' 
                    : alert?.isActive 
                      ? 'border-border bg-background' :'border-border bg-muted/30'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-brand-sm flex items-center justify-center ${
                    alert?.token === 'AOGX' ?'bg-gradient-to-br from-primary to-accent' :'bg-muted'
                  }`}>
                    <span className={`text-sm font-bold ${
                      alert?.token === 'AOGX' ? 'text-white' : 'text-foreground'
                    }`}>
                      {alert?.token?.charAt(0)}
                    </span>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-foreground">{alert?.token}</p>
                      {isTriggered && (
                        <div className="bg-warning text-warning-foreground px-2 py-1 rounded-brand-xs text-xs font-medium">
                          Triggered
                        </div>
                      )}
                      {!alert?.isActive && (
                        <div className="bg-muted text-muted-foreground px-2 py-1 rounded-brand-xs text-xs">
                          Paused
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Alert when price goes {alert?.condition} ${alert?.price?.toFixed(4)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Current: ${alert?.currentPrice?.toFixed(4)} • Created {alert?.createdAt?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleAlert(alert?.id)}
                    className="h-8 w-8"
                  >
                    <Icon 
                      name={alert?.isActive ? "Pause" : "Play"} 
                      size={16} 
                    />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteAlert(alert?.id)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Icon name="Smartphone" size={16} />
            <span>Mobile app notifications available</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Mail" size={16} />
            <span>Email alerts sent instantly</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceAlerts;